import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  Container,
  IconButton,
  Button,
  Switch,
  FormControlLabel,
  InputAdornment,
  FormControl,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import { Typography, TextField, Autocomplete } from "@mui/material";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import { listTimeZones } from "timezone-support";
import { useTranslation } from "react-i18next";
import { myDesignService } from "../../../services";
import clsx from "clsx";
import ConfirmationDialog from "../../../components/dialog/ConfirmationDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import Skeleton from "@mui/material/Skeleton";
import { QRCode } from "react-qrcode-logo";
import { pictoWebVr } from "../../../helpers";
import { CopyToClipboard } from "react-copy-to-clipboard";
import LinkIcon from "@mui/icons-material/Link";
import ShareIcon from "@mui/icons-material/Share";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { openBackdrop } from "../../../reducers/myDesigns/myDesignsSlice";
import {
  associateImageMyDesign,
  deleteTarget,
  updateTarget,
  updateTimezone,
  sendNotification,
} from "../../../reducers/myDesigns/myDesignsThunks";
import GoldenSwitch from "../../../components/goldenSwitch/GoldenSwitch";

const PREFIX = "Targets";

const classes = {
  switchBase: `${PREFIX}-switchBase`,
  checked: `${PREFIX}-checked`,
  track: `${PREFIX}-track`,
  root: `${PREFIX}-root`,
  autocomplete: `${PREFIX}-autocomplete`,
  targetUrl: `${PREFIX}-targetUrl`,
  targetUrlContainer: `${PREFIX}-targetUrlContainer`,
  targetUrlContainerPainting: `${PREFIX}-targetUrlContainerPainting`,
  updateButton: `${PREFIX}-updateButton`,
  ADSButtonContainer: `${PREFIX}-ADSButtonContainer`,
  actionButton: `${PREFIX}-actionButton`,
  ADSIconButton: `${PREFIX}-ADSIconButton`,
  iconButtonContainer: `${PREFIX}-iconButtonContainer`,
  iconButtonContainerNotify: `${PREFIX}-iconButtonContainerNotify`,
  NotifyIcon: `${PREFIX}-NotifyIcon`,
  ExternalLinkIcon: `${PREFIX}-ExternalLinkIcon`,
  displayIcons: `${PREFIX}-displayIcons`,
  deleteTargetContainer: `${PREFIX}-deleteTargetContainer`,
  displayChangeImageContainer: `${PREFIX}-displayChangeImageContainer`,
  editImageIcon: `${PREFIX}-editImageIcon`,
  imageMargin: `${PREFIX}-imageMargin`,
  mainDisplayContainer: `${PREFIX}-mainDisplayContainer`,
  optionsContainer: `${PREFIX}-optionsContainer`,
  toggleBorderlessContainer: `${PREFIX}-toggleBorderlessContainer`,
  borderlessToggle: `${PREFIX}-borderlessToggle`,
  backgroundIcon: `${PREFIX}-backgroundIcon`,
  skeletonRoot: `${PREFIX}-skeletonRoot`,
  qrPosition: `${PREFIX}-qrPosition`,
  buttonsQrCode: `${PREFIX}-buttonsQrCode`,
  downloadQRButton: `${PREFIX}-downloadQRButton`,
  shareIconButton: `${PREFIX}-shareIconButton`,
  copyQrUrlButton: `${PREFIX}-copyQrUrlButton`,
  qrContainer: `${PREFIX}-qrContainer`,
  targetUrlForm: `${PREFIX}-targetUrlForm`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    padding: "0 0 1rem 0",
    textAlign: "center",
  },

  [`& .${classes.autocomplete}`]: {
    width: "100%",
    maxWidth: "350px",
    margin: "1rem 0",
  },

  [`& .${classes.targetUrl}`]: {
    width: "100%",
    maxWidth: "350px",
    margin: "1rem 0",
  },

  [`& .${classes.targetUrlContainer}`]: {
    maxWidth: "350px",
    marginBottom: "1rem",
    [theme.breakpoints.between("xs", "md")]: {
      position: "relative",
      left: "50%",
      transform: "translate(-50%)",
      marginBottom: "0",
    },
  },

  [`& .${classes.targetUrlContainerPainting}`]: {
    maxWidth: "350px",
    [theme.breakpoints.between("xs", "md")]: {
      marginTop: "-0.5rem",
      position: "relative",
      left: "50%",
      transform: "translate(-50%)",
    },
  },

  [`& .${classes.updateButton}`]: {
    maxWidth: "350px",
    width: "100%",
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "3rem",
    },
  },

  [`& .${classes.ADSButtonContainer}`]: {
    position: "relative",
    width: "100%",
    margin: "0.25rem 0 0.5rem 0",
    backgroundColor: theme.palette.primary.main,
    paddingRight: "1rem",
    borderRadius: "4px",
    maxHeight: "56px",
    marginTop: "1rem",
    [theme.breakpoints.between("xs", "md")]: {
      marginTop: "0",
      left: "50%",
      marginBottom: "0",
      transform: "translate(-50%)",
    },
  },

  [`& .${classes.actionButton}`]: {
    margin: "0.5rem 0 0.5rem 0",
    backgroundColor: theme.palette.primary.main,
  },

  [`& .${classes.ADSIconButton}`]: {
    width: "45px",
    height: "43px",
    color: "rgba(255,255,255,1)",
  },

  [`& .${classes.iconButtonContainer}`]: {
    margin: "1rem -0.5rem 0.5rem 1rem",
    position: "relative",
    width: "50px",
  },

  [`& .${classes.iconButtonContainerNotify}`]: {
    margin: "1rem -0.5rem 0.5rem 1rem",
    position: "relative",
    width: "50px",
  },

  [`& .${classes.NotifyIcon}`]: {
    width: "45px",
    height: "43px",
    position: "absolute",
    top: "50%",
    right: "6px",
    transform: "translateY(-50%)",
    color: "rgb(215, 175, 95)",
  },

  [`& .${classes.ExternalLinkIcon}`]: {
    width: "50px",
    height: "48px",
    position: "absolute",
    top: "50%",
    right: 0,
    transform: "translateY(-50%)",
    color: "rgb(215, 175, 95)",
  },

  [`& .${classes.displayIcons}`]: {
    display: "flex",
    justifyContent: "space-around",
    [theme.breakpoints.up("sm")]: {
      position: "relative",
      left: "50%",
      transform: "translateX(-50%)",
    },
  },

  [`& .${classes.deleteTargetContainer}`]: {
    marginTop: "0.5rem",
  },

  [`& .${classes.displayChangeImageContainer}`]: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    maxWidth: "350px",
    margin: "0 auto",
  },

  [`& .${classes.editImageIcon}`]: {
    position: "absolute",
    right: "0",
  },

  [`& .${classes.imageMargin}`]: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "50%",
    minWidth: "50%",
    [theme.breakpoints.between("xs", "md")]: {
      maxWidth: "100%",
    },
  },

  [`& .${classes.mainDisplayContainer}`]: {
    display: "flex",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.optionsContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  [`& .${classes.toggleBorderlessContainer}`]: {
    textAlign: "center",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  [`& .${classes.borderlessToggle}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.backgroundIcon}`]: {
    backgroundColor: "white",
    margin: "0.5rem",
  },

  [`& .${classes.skeletonRoot}`]: {
    width: "100%",
    height: "300px",
    maxWidth: "350px",
    borderRadius: "10px",
    marginBottom: "1rem",
  },

  [`& .${classes.qrPosition}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.buttonsQrCode}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.downloadQRButton}`]: {
    margin: "1rem 0",
  },

  [`& .${classes.shareIconButton}`]: {
    color: "#343434",
    backgroundColor: theme.palette.primary.main,
    width: "40px",
    height: "40px",
    margin: "1rem 0",
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: "#343434",
    },
  },

  [`& .${classes.copyQrUrlButton}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.qrContainer}`]: {
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexDirection: "column",
      width: "350px",
      maxWidth: "100%",
      margin: "0 auto",
    },
    maxWidth: "350px",
    [theme.breakpoints.up("sm")]: {
      marginTop: "1rem",
    },
  },

  [`& .${classes.targetUrlForm}`]: {
    width: "100%",
    margin: "1rem 0",
  },
}));

export default function Targets(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [blinklProvider, setBlinklProvider] = React.useState("");
  const [blinklCampaign, setBlinklCampaign] = React.useState("");
  const [targetUrl, setTargetUrl] = React.useState("");
  const [imageError, setImageError] = useState(true);
  const [borderless, setBorderless] = useState(false);
  const [clipboard, setClipboard] = React.useState({
    value: "",
    copied: false,
  });
  const [borderlessImageError, setBorderlessImageError] = useState(true);
  const [copied, setCopied] = useState(false);
  const [picto, setPicto] = useState(null);
  const canvasRef = useRef(null);
  const loading = useSelector((state) => state.myDesigns.loading);
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const targets = useSelector(
    (state) => state.myDesigns.currentDesign?.targets
  );

  const [saving, setSaving] = useState(false);
  const [imageLoaders, setImageLoaders] = useState({
    classic: true,
    borderless: true,
  });

  useEffect(() => {
    setPicto(pictoWebVr);
  }, []);

  const mySendNotification = async () => {
    try {
      const thunk = await dispatch(
        sendNotification({
          frameId: props.frameId,
          frameName: props.frameName,
          label: props.selectedTarget.label,
          zoneName: null,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
      enqueueSnackbar(t("toasts.notificationSent"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const myUpdateTimezone = async (value) => {
    const tmp = [];
    props.targets.forEach((target) => {
      if (target.id === props.selectedTarget.id) {
        tmp.push({ ...target, timeZone: value.label });
      } else tmp.push(target);
    });
    try {
      const thunk = await dispatch(
        updateTimezone({
          frameId: props.frameId,
          targetId: props.selectedTarget.id,
          timeZone: value,
          targets: tmp,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
      props.setSelectedTarget({
        ...props.selectedTarget,
        timeZone: value.label,
      });
      enqueueSnackbar(t("toasts.timezoneModified"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const updateInformations = async () => {
    const tmp = {
      ...props.selectedTarget,
      blinklProvider,
      blinklCampaign,
      targetUrl,
    };
    try {
      const thunk = await dispatch(
        updateTarget({ targetId: tmp.id, target: tmp, targetLabel: tmp.label })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      enqueueSnackbar(t("toasts.updateTargetSuccess"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("toasts.updateTargetError"), { variant: "error" });
    }
  };

  const shareMedia = () => {
    navigator.share({
      title: document.title,
      text: `${t("myDesign.webVR.shareExparienceStart")}${
        props.selectedTarget.label
      }${t("myDesign.webVR.shareExparienceEnd")}`,
      url: props.selectedTarget.shortLink,
    });
  };

  const downloadQR = () => {
    let canvas = canvasRef.current.querySelector("canvas");
    let image = canvas.toDataURL("image/jpg");
    let anchor = document.createElement("a");
    anchor.href = image;
    anchor.download = `QRCode-${props.selectedTarget.label.replace(
      /\s/g,
      ""
    )}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  useEffect(() => {
    if (props.selectedTarget) {
      let tmp = props.selectedTarget.targetUrl || "";
      setBlinklProvider(props.selectedTarget.blinklProvider);
      setBlinklCampaign(props.selectedTarget.blinklCampaign);
      setClipboard({ ...clipboard, value: tmp });
      setTargetUrl(tmp);
    }
  }, [props.selectedTarget]);

  useEffect(() => {
    if (props.currentDesign && props.currentDesign.imageUrl)
      setImageError(false);
    if (props.currentDesign && props.currentDesign.imageBorderlessUrl)
      setBorderlessImageError(false);
  }, [props.currentDesign]);

  return (
    <StyledDiv style={{ width: "100%" }}>
      <Container className={classes.root}>
        {isAdmin && (
          <ConfirmationDialog
            open={openConfirmation}
            setOpen={setOpenConfirmation}
            element={props.selectedTarget}
            onAgree={async () => {
              try {
                const thunk = await dispatch(
                  deleteTarget({
                    frameId: props.frameId,
                    target: props.selectedTarget,
                  })
                );
                if (thunk.meta.requestStatus === "rejected")
                  throw new Error("offline");
                props.setSelectedTarget(targets[0]);
                enqueueSnackbar(t("toasts.deleteTargetSuccess"), {
                  variant: "success",
                });
              } catch {
                enqueueSnackbar(t("toasts.deleteTargetError"), {
                  variant: "error",
                });
              }
              if (targets.length === 1) {
                props.setSelectedTarget(null);
              }
            }}
            mode="deleteTarget"
          />
        )}
        {props.selectedTarget ? (
          <React.Fragment>
            <div className={classes.mainDisplayContainer}>
              <div className={clsx(classes.imageMargin)}>
                <div className={classes.toggleBorderlessContainer}>
                  <FormControlLabel
                    control={
                      <GoldenSwitch
                        checked={borderless}
                        onChange={(e) => setBorderless(e.target.checked)}
                        disabled={saving}
                        name="borderless"
                        classes={{
                          switchBase: classes.switchBase,
                          checked: classes.checked,
                          track: classes.track,
                        }}
                      />
                    }
                    label={
                      borderless
                        ? t("myDesign.informations.borderlessOff")
                        : t("myDesign.informations.borderless")
                    }
                    className={classes.borderlessToggle}
                  />
                  <Typography variant="caption">
                    {!borderless
                      ? t("myDesign.informations.borderlessExplanations")
                      : t("myDesign.informations.borderlessOffExplanations")}
                  </Typography>
                </div>
                {!borderless ? (
                  <div id="borderlessImageContainer">
                    {borderlessImageError ? (
                      <div>
                        <p>{t("myDesign.informations.noPicture")}</p>
                        {isAdmin && (
                          <Button
                            variant="contained"
                            color="primary"
                            component="label"
                            className={classes.uploadButton}
                            onChange={(e) => {
                              setSaving(true);
                              myDesignService
                                .associateBorderlessImageMyDesign(
                                  e.target.files,
                                  props.frameId
                                )
                                .then(() => {
                                  setBorderlessImageError(false);
                                  props.setBorderlessUrl(
                                    URL.createObjectURL(e.target.files[0])
                                  );
                                  enqueueSnackbar(
                                    t("toasts.associateBorderlessImageSuccess"),
                                    { variant: "success" }
                                  );
                                  setSaving(false);
                                })
                                .catch((err) => {
                                  if (err === 403)
                                    enqueueSnackbar(
                                      t("toasts.unauthorizedError"),
                                      {
                                        variant: "error",
                                      }
                                    );
                                  else
                                    enqueueSnackbar(
                                      t("toasts.associateBorderlessImageError"),
                                      { variant: "error" }
                                    );
                                  setSaving(false);
                                });
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {t("myDesign.zones.uploadVideo.selectFileButton")}
                            <input
                              type="file"
                              accept="image/*"
                              name="myDesignBorderlessImage"
                              hidden
                            />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className={classes.displayChangeImageContainer}>
                        {saving ? (
                          <div style={{ margin: "1rem 0" }}>
                            <CircularProgress size={50} />
                            <Typography variant="body1">
                              {t("loaders.saving")}
                            </Typography>
                          </div>
                        ) : (
                          <div>
                            <img
                              src={props.borderlessUrl}
                              alt="borderless painting"
                              key={props.borderlessUrl}
                              style={{
                                width: "100%",
                                maxWidth: "350px",
                                borderRadius: "10px",
                                marginBottom: "1rem",
                              }}
                              onError={(e) => setBorderlessImageError(true)}
                              onLoad={() =>
                                setImageLoaders({
                                  ...imageLoaders,
                                  borderless: false,
                                })
                              }
                            />
                            {imageLoaders.borderless && (
                              <Skeleton
                                variant="rectangular"
                                className={classes.skeletonRoot}
                              />
                            )}
                          </div>
                        )}
                        {isAdmin && !saving && !imageLoaders.borderless && (
                          <div className={classes.editImageIcon}>
                            <input
                              type="file"
                              accept="image/*"
                              name="myDesignBorderlessImage"
                              id="icon-button-file"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                setSaving(true);
                                myDesignService
                                  .associateBorderlessImageMyDesign(
                                    e.target.files,
                                    props.frameId
                                  )
                                  .then(() => {
                                    setBorderlessImageError(false);
                                    props.setBorderlessUrl(
                                      URL.createObjectURL(e.target.files[0])
                                    );
                                    enqueueSnackbar(
                                      t(
                                        "toasts.associateBorderlessImageSuccess"
                                      ),
                                      { variant: "success" }
                                    );
                                    setSaving(false);
                                  })
                                  .catch((err) => {
                                    if (err === 403)
                                      enqueueSnackbar(
                                        t("toasts.unauthorizedError"),
                                        {
                                          variant: "error",
                                        }
                                      );
                                    else
                                      enqueueSnackbar(
                                        t(
                                          "toasts.associateBorderlessImageError"
                                        ),
                                        { variant: "error" }
                                      );
                                    setSaving(false);
                                  });
                              }}
                            />
                            <label htmlFor="icon-button-file">
                              <IconButton
                                color="primary"
                                aria-label="upload picture borderless"
                                component="span"
                                className={classes.backgroundIcon}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                size="large"
                              >
                                <CreateIcon />
                              </IconButton>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div id="imageWithBorderContainer">
                    {imageError ? (
                      <div>
                        <p>{t("myDesign.informations.noPicture")}</p>
                        {isAdmin && (
                          <Button
                            variant="contained"
                            color="primary"
                            component="label"
                            className={classes.uploadButton}
                            onChange={async (e) => {
                              try {
                                const thunk = await dispatch(
                                  associateImageMyDesign({
                                    files: e.target.files,
                                    frameId: props.frameId,
                                  })
                                );
                                if (thunk.meta.requestStatus === "rejected")
                                  throw new Error("offline");
                                setImageError(false);
                                props.setUrl(
                                  URL.createObjectURL(e.target.files[0])
                                );
                              } catch {
                                enqueueSnackbar(t("toasts.unknownError"), {
                                  variant: "error",
                                });
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {t("myDesign.zones.uploadVideo.selectFileButton")}
                            <input
                              type="file"
                              accept="image/*"
                              name="myDesignImage"
                              hidden
                            />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className={classes.displayChangeImageContainer}>
                        {loading && <CircularProgress size={50} />}
                        <Box
                          sx={{
                            display:
                              loading || imageLoaders.classic
                                ? "none"
                                : "block",
                          }}
                        >
                          <img
                            src={props.url}
                            alt="painting"
                            key={props.url}
                            style={{
                              width: "100%",
                              maxWidth: "350px",
                              borderRadius: "10px",
                              marginBottom: "1rem",
                            }}
                            onError={(e) => setImageError(true)}
                            onLoad={() => {
                              setImageLoaders({
                                ...imageLoaders,
                                classic: false,
                              });
                            }}
                          />
                          {imageLoaders.classic && (
                            <Skeleton
                              variant="rectangular"
                              className={classes.skeletonRoot}
                            />
                          )}
                        </Box>
                        {isAdmin && !loading && !imageLoaders.classic && (
                          <div className={classes.editImageIcon}>
                            <input
                              type="file"
                              accept="image/*"
                              name="myDesignImage"
                              id="icon-button-file"
                              style={{ display: "none" }}
                              onChange={async (e) => {
                                try {
                                  const thunk = await dispatch(
                                    associateImageMyDesign({
                                      files: e.target.files,
                                      frameId: props.frameId,
                                    })
                                  );
                                  if (thunk.meta.requestStatus === "rejected")
                                    throw new Error("offline");
                                  props.setUrl(
                                    URL.createObjectURL(e.target.files[0])
                                  );
                                } catch {
                                  enqueueSnackbar(t("toasts.unknownError"), {
                                    variant: "error",
                                  });
                                }
                              }}
                            />
                            <label htmlFor="icon-button-file">
                              <IconButton
                                color="primary"
                                aria-label="upload picture"
                                component="span"
                                className={classes.backgroundIcon}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                size="large"
                              >
                                <CreateIcon />
                              </IconButton>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <Box
                  className={clsx(
                    classes.targetUrlContainer,
                    classes.displayIcons
                  )}
                >
                  <IconButton
                    color="inherit"
                    aria-label="send notification"
                    edge="end"
                    onClick={() => {
                      mySendNotification();
                    }}
                    className={classes.actionButton}
                    size="large"
                  >
                    <SettingsInputAntennaIcon
                      className={classes.ADSIconButton}
                    />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    aria-label="preview"
                    edge="end"
                    onClick={() => {
                      if (props.selectedTarget.targetUrl === targetUrl)
                        window.open(props.selectedTarget.targetUrl, "_blank");
                      else window.open(targetUrl, "_blank");
                    }}
                    className={classes.actionButton}
                    size="large"
                  >
                    <FindInPageIcon className={classes.ADSIconButton} />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    aria-label="open in mobile application"
                    edge="end"
                    onClick={() =>
                      dispatch(openBackdrop({ from: "ADS-app", infos: {} }))
                    }
                    className={classes.actionButton}
                    size="large"
                  >
                    <PhoneIphoneIcon className={classes.ADSIconButton} />
                  </IconButton>
                  {isAdmin && (
                    <IconButton
                      color="inherit"
                      aria-label="open in mobile application"
                      edge="end"
                      onClick={() => setOpenConfirmation(true)}
                      className={classes.actionButton}
                      size="large"
                    >
                      <DeleteIcon className={classes.ADSIconButton} />
                    </IconButton>
                  )}
                </Box>
              </div>
              <div className={classes.optionsContainer}>
                <Box
                  className={classes.targetUrlContainer}
                  style={{ marginBottom: "0.5rem" }}
                >
                  <Autocomplete
                    label={t("myDesign.informations.timezone")}
                    id="autocomplete-tz"
                    disableClearable
                    className={classes.autocomplete}
                    options={listTimeZones().map((tz) => ({ label: tz }))}
                    getOptionLabel={(option) =>
                      option.label !== undefined && option.label !== ""
                        ? option.label
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.label
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("myDesign.informations.timezone")}
                        variant="outlined"
                      />
                    )}
                    value={{ label: props.selectedTarget.timeZone }}
                    onChange={(e, value) => {
                      myUpdateTimezone(value);
                    }}
                  />
                </Box>
                <Box className={classes.targetUrlContainerPainting}>
                  <TextField
                    disabled
                    variant="outlined"
                    margin="normal"
                    id="Painting code"
                    label={t("myDesign.informations.paintingCode")}
                    name="Painting code"
                    autoComplete="Painting code"
                    value={`${props.frameId}_${props.selectedTarget.label}`}
                    className={classes.targetUrl}
                    style={{ marginBottom: "0.5rem", marginTop: "0" }}
                  />
                </Box>
                {isAdmin && (
                  <React.Fragment>
                    <Box className={classes.targetUrlContainerPainting}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="blink campaign id"
                        label={t("myDesign.informations.blinklCampaignId")}
                        name="Campaign id"
                        autoComplete="Campaign id"
                        value={!blinklCampaign ? "" : blinklCampaign}
                        onChange={(e) => setBlinklCampaign(e.target.value)}
                        className={classes.targetUrl}
                        style={{ margin: "1rem 0" }}
                      />
                    </Box>
                    <Box className={classes.targetUrlContainerPainting}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="blink provider id"
                        label={t("myDesign.informations.blinklProviderId")}
                        name="Provider id"
                        autoComplete="Provider id"
                        value={!blinklProvider ? "" : blinklProvider}
                        onChange={(e) => setBlinklProvider(e.target.value)}
                        className={classes.targetUrl}
                        style={{ margin: "0.5rem 0" }}
                      />
                    </Box>
                    <Box className={classes.targetUrlContainerPainting}>
                      <FormControl
                        variant="outlined"
                        className={classes.targetUrlForm}
                      >
                        <InputLabel htmlFor="outlined-adornment-password">
                          {t("myDesign.informations.targetUrl")}
                        </InputLabel>
                        <OutlinedInput
                          id="outlined-adornment-password"
                          label={t("myDesign.informations.targetUrl")}
                          type="text"
                          value={targetUrl}
                          onChange={(e) => {
                            setTargetUrl(e.target.value);
                          }}
                          endAdornment={
                            <InputAdornment position="end">
                              <CopyToClipboard
                                onCopy={() =>
                                  setClipboard({ ...clipboard, copied: true })
                                }
                                text={targetUrl}
                              >
                                <IconButton
                                  className={classes.copyLinkButton}
                                  aria-label="copy link"
                                  color="primary"
                                  edge="end"
                                  onClick={(e) => {
                                    enqueueSnackbar(
                                      t("toasts.copiedInClipboard"),
                                      { variant: "info" }
                                    );
                                    e.stopPropagation();
                                  }}
                                  size="large"
                                >
                                  <LinkIcon />
                                </IconButton>
                              </CopyToClipboard>
                              <a
                                href={targetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <IconButton
                                  className={classes.copyLinkButton}
                                  aria-label="copy link"
                                  color="primary"
                                  edge="end"
                                  size="large"
                                >
                                  <OpenInNewIcon />
                                </IconButton>
                              </a>
                            </InputAdornment>
                          }
                          labelWidth={70}
                        />
                      </FormControl>
                    </Box>
                    {isAdmin && (
                      <Box className={classes.targetUrlContainerPainting}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => updateInformations()}
                          className={classes.updateButton}
                        >
                          {t("myDesign.informations.update")}
                        </Button>
                      </Box>
                    )}
                    <div className={classes.qrContainer}>
                      <div className={classes.qrPosition}>
                        {props.selectedTarget?.targetUrl && picto && (
                          <CopyToClipboard
                            text={props.selectedTarget.targetUrl}
                            onCopy={() => {
                              setCopied(true);
                              enqueueSnackbar(t("toasts.copiedInClipboard"), {
                                variant: "info",
                              });
                            }}
                          >
                            <div ref={canvasRef}>
                              <QRCode
                                id="react-qrcode-logo"
                                // value={props.selectedTarget.arLink || "app.fanartzone.com"}
                                value={props.selectedTarget.targetUrl}
                                size={250}
                                ecLevel="H"
                                bgColor="#000000"
                                fgColor="#D3AF5F" //golden
                                includeMargin={true}
                                qrStyle="dots"
                                logoImage={picto}
                                logoHeight={65}
                                logoWidth={65}
                                eyeRadius={[
                                  [0, 100, 100, 100],
                                  [100, 0, 100, 100],
                                  [100, 100, 100, 0],
                                ]}
                                logoOpacity={1} // the higher the opacity is, the lower the logo has to be
                              />
                            </div>
                          </CopyToClipboard>
                        )}
                      </div>
                      {props.selectedTarget?.targetUrl && picto && (
                        <div>
                          <div className={classes.buttonsQrCode}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={downloadQR}
                              className={classes.downloadQRButton}
                            >
                              {t("myDesign.webVR.downloadQR")}
                            </Button>
                            <a
                              href={props.selectedTarget.targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconButton
                                className={classes.shareIconButton}
                                size="large"
                              >
                                <LinkIcon />
                              </IconButton>
                            </a>
                            <IconButton
                              className={classes.shareIconButton}
                              onClick={() => shareMedia()}
                              size="large"
                            >
                              <ShareIcon />
                            </IconButton>
                          </div>
                          <div className={classes.copyQrUrlButton}>
                            <CopyToClipboard
                              text={props.selectedTarget.targetUrl}
                              onCopy={() => {
                                setCopied(true);
                                enqueueSnackbar(t("toasts.copiedInClipboard"), {
                                  variant: "info",
                                });
                              }}
                            >
                              <Button variant="contained" color="primary">
                                {t("myDesign.webVR.copyShortLink")}
                              </Button>
                            </CopyToClipboard>
                          </div>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
          </React.Fragment>
        ) : props.targets && props.targets.length === 0 && !loading ? (
          <div>{t("myDesign.informations.noTargetsCreated")}</div>
        ) : (
          <Container>
            <div className="loading-container">
              <CircularProgress size={50} />
              <Typography>{t("loaders.fetching")}</Typography>
            </div>
          </Container>
        )}
      </Container>
    </StyledDiv>
  );
}
